import {
  GarageTypes,
  HeatingTypes,
  PropertyPurposeTypes,
  PropertyStyles,
  PropertyTenureTypes,
  PropertyTypes,
} from "@nestoca/multi-tenant";

import {
  CoOwnershipFeesFrequency,
  FundPurposeEnum,
  GarageSizeEnum,
  GarageTypeEnum,
  HeatingTypeEnum,
  SewageTypeEnum,
  SpaceUnitsEnum,
  TargetPropertyConstructionTypeEnum,
  TargetPropertyPurposeEnum,
  TargetPropertyStyleEnum,
  TargetPropertyTenureEnum,
  TargetPropertyTypeEnum,
  UnitType,
  WaterTypeEnum,
} from "./property.enum";

type PropertyPurposeValue =
  (typeof PropertyPurposeTypes)[keyof typeof PropertyPurposeTypes];
type PropertyTypeValue = (typeof PropertyTypes)[keyof typeof PropertyTypes];
type PropertyStyleValue = (typeof PropertyStyles)[keyof typeof PropertyStyles];
type PropertyTenureValue =
  (typeof PropertyTenureTypes)[keyof typeof PropertyTenureTypes];
type HeatingTypeValue = (typeof HeatingTypes)[keyof typeof HeatingTypes];
type GarageTypeValue = (typeof GarageTypes)[keyof typeof GarageTypes];

export const SUBJECT_PROPERTY_PURPOSE_LABEL: Partial<
  Record<PropertyPurposeValue, string>
> = {
  [PropertyPurposeTypes.OWNER_OCCUPIED]:
    "target-property:purpose.ownerOccupied",
  [PropertyPurposeTypes.OWNER_OCCUPIED_AND_RENTAL]:
    "target-property:purpose.ownerOccupiedRental",
  [PropertyPurposeTypes.RENTAL]: "target-property:purpose.rental",
  [PropertyPurposeTypes.SECONDARY_RESIDENCE]:
    "target-property:purpose.secondaryResidence",
  [PropertyPurposeTypes.SEASONAL_HOME]: "target-property:purpose.seasonalHome",
  [PropertyPurposeTypes.RECREATIONAL_PROPERTY]:
    "target-property:purpose.recreationalProperty",
  [PropertyPurposeTypes.RESERVE_PROPERTY]:
    "target-property:purpose.reserveProperty",
};

export const PROPERTY_TYPES_LABEL: Partial<Record<PropertyTypeValue, string>> =
  {
    [PropertyTypes.DETACHED]: "target-property:type.detached",
    [PropertyTypes.SEMI_DETACHED]: "target-property:type.semi-detached",
    [PropertyTypes.DUPLEX_DETACHED]: "target-property:type.duplex-detached",
    [PropertyTypes.DUPLEX_SEMI_DETACHED]:
      "target-property:type.duplex-semi-detached",
    [PropertyTypes.ROW_HOUSING]: "target-property:type.row-housing",
    [PropertyTypes.APARTMENT_LOW_RISE]:
      "target-property:type.apartment-low-rise",
    [PropertyTypes.APARTMENT_HIGH_RISE]:
      "target-property:type.apartment-hi-rise",
    [PropertyTypes.MOBILE]: "target-property:type.mobile",
    [PropertyTypes.TRI_PLEX_DETACHED]: "target-property:type.tri-plex-detached",
    [PropertyTypes.TRI_PLEX_SEMI_DETACHED]:
      "target-property:type.tri-plex-semi-detached",
    [PropertyTypes.STACKED]: "target-property:type.stacked",
    [PropertyTypes.MODULAR_HOME_DETACHED]:
      "target-property:type.modular-home-detached",
    [PropertyTypes.MODULAR_HOME_SEMI_DETACHED]:
      "target-property:type.modular-home-semi-detached",
    [PropertyTypes.FOUR_PLEX_DETACHED]:
      "target-property:type.four-plex-detached",
    [PropertyTypes.FOUR_PLEX_SEMI_DETACHED]:
      "target-property:type.four-plex-semi-detached",
    [PropertyTypes.SeasonalHome]: "target-property:type.seasonal-home",
    [PropertyTypes.Condominium]: "target-property:type.condominium",
  };

export const PROPERTY_STYLES_LABEL: Partial<
  Record<PropertyStyleValue, string>
> = {
  [PropertyStyles.ONE_STOREY]: "target-property:style.bungalow",
  [PropertyStyles.BI_LEVEL]: "target-property:style.biLevel",
  [PropertyStyles.TWO_STORY]: "target-property:style.twoStory",
  [PropertyStyles.SPLIT_LEVEL]: "target-property:style.split",
  [PropertyStyles.STOREY_AND_A_HALF]: "target-property:style.storeyHalf",
  [PropertyStyles.THREE_STOREY]: "target-property:style.threeStorey",
  [PropertyStyles.OTHER]: "target-property:style.other",
};

export const PROPERTY_TENURE_LABEL: Partial<
  Record<PropertyTenureValue, string>
> = {
  [PropertyTenureTypes.FREE_HOLD]: "target-property:tenure.freeHold",
  [PropertyTenureTypes.CONDO]: "target-property:tenure.condo",
  [PropertyTenureTypes.LEASE_HOLD]: "target-property:tenure.leaseHold",
  [PropertyTenureTypes.FREEHOLD_CONDO]: "target-property:tenure.freeholdCondo",
  [PropertyTenureTypes.LEASEHOLD_CONDO]:
    "target-property:tenure.leaseholdCondo",
  [PropertyTenureTypes.RESERVE]: "target-property:tenure.reserve",
};

export const HEATING_TYPES_LABEL: Partial<Record<HeatingTypeValue, string>> = {
  [HeatingTypes.ELECTRIC]: "target-property:heating.electric",
  [HeatingTypes.FORCED_AIR_OIL_GAS_ELECTRIC]:
    "target-property:heating.forcedAirOilGasElectric",
  [HeatingTypes.HOT_WATER]: "target-property:heating.hotWater",
  [HeatingTypes.OTHER]: "target-property:heating.other",
  [HeatingTypes.NATURAL_GAS]: "target-property:heating.naturalGas",
  [HeatingTypes.OIL]: "target-property:heating.oil",
  [HeatingTypes.SOLAR_PANELS]: "target-property:heating.solarPanels",
  [HeatingTypes.WIND]: "target-property:heating.wind",
  [HeatingTypes.GEOTHERMAL]: "target-property:heating.geothermal",
  [HeatingTypes.WOOD]: "target-property:heating.wood",
};

export const GARAGE_TYPES_LABEL: Partial<Record<GarageTypeValue, string>> = {
  [GarageTypes.DETACHED]: "target-property:garage.type.detached",
  [GarageTypes.ATTACHED]: "target-property:garage.type.attached",
  [GarageTypes.UNCOVERED_OWNED_RESERVED]:
    "target-property:garage.type.uncoveredOwnedReserved",
};

export const PURPOSE_OPTIONS = [
  {
    label: "target-property:purpose.ownerOccupied",
    value: TargetPropertyPurposeEnum.OWNER_OCCUPIED,
  },
  {
    label: "target-property:purpose.ownerOccupiedRental",
    value: TargetPropertyPurposeEnum.OWNER_OCCUPIED_AND_RENTAL,
  },
  {
    label: "target-property:purpose.rental",
    value: TargetPropertyPurposeEnum.RENTAL,
  },
  {
    label: "target-property:purpose.secondaryResidence",
    value: TargetPropertyPurposeEnum.SECONDARY_RESIDENCE,
  },
];

export const PROPERTY_TYPE = [
  {
    label: "target-property:type.detached",
    value: TargetPropertyTypeEnum.DETACHED,
  },
  {
    label: "target-property:type.semi-detached",
    value: TargetPropertyTypeEnum.SEMI_DETACHED,
  },
  {
    label: "target-property:type.duplex-detached",
    value: TargetPropertyTypeEnum.DUPLEX_DETACHED,
  },
  {
    label: "target-property:type.duplex-semi-detached",
    value: TargetPropertyTypeEnum.DUPLEX_SEMI_DETACHED,
  },
  {
    label: "target-property:type.row-housing",
    value: TargetPropertyTypeEnum.ROW_HOUSING,
  },
  {
    label: "target-property:type.apartment-low-rise",
    value: TargetPropertyTypeEnum.APARTMENT_LOW_RISE,
  },
  {
    label: "target-property:type.apartment-hi-rise",
    value: TargetPropertyTypeEnum.APARTMENT_HIGH_RISE,
  },
  {
    label: "target-property:type.mobile",
    value: TargetPropertyTypeEnum.MOBILE,
  },
  {
    label: "target-property:type.tri-plex-detached",
    value: TargetPropertyTypeEnum.TRI_PLEX_DETACHED,
  },
  {
    label: "target-property:type.tri-plex-semi-detached",
    value: TargetPropertyTypeEnum.TRI_PLEX_SEMI_DETACHED,
  },
  {
    label: "target-property:type.stacked",
    value: TargetPropertyTypeEnum.STACKED,
  },
  {
    label: "target-property:type.modular-home-detached",
    value: TargetPropertyTypeEnum.MODULAR_HOME_DETACHED,
  },
  {
    label: "target-property:type.modular-home-semi-detached",
    value: TargetPropertyTypeEnum.MODULAR_HOME_SEMI_DETACHED,
  },
  {
    label: "target-property:type.four-plex-detached",
    value: TargetPropertyTypeEnum.FOUR_PLEX_DETACHED,
  },
  {
    label: "target-property:type.four-plex-semi-detached",
    value: TargetPropertyTypeEnum.FOUR_PLEX_SEMI_DETACHED,
  },
  {
    label: "target-property:type.seasonal-home",
    value: TargetPropertyTypeEnum.SEASONAL_HOME,
  },
];

export const PROPERTY_STYLE = [
  {
    value: TargetPropertyStyleEnum.ONE_STOREY,
    label: "target-property:style.bungalow",
  },
  {
    value: TargetPropertyStyleEnum.BI_LEVEL,
    label: "target-property:style.biLevel",
  },
  {
    value: TargetPropertyStyleEnum.TWO_STORY,
    label: "target-property:style.twoStory",
  },
  {
    value: TargetPropertyStyleEnum.SPLIT_LEVEL,
    label: "target-property:style.split",
  },
  {
    value: TargetPropertyStyleEnum.STOREY_AND_A_HALF,
    label: "target-property:style.storeyHalf",
  },
  {
    value: TargetPropertyStyleEnum.THREE_STOREY,
    label: "target-property:style.threeStorey",
  },
  {
    value: TargetPropertyStyleEnum.OTHER,
    label: "target-property:style.other",
  },
];

export const CONSTRUCTION_TYPE = [
  {
    value: TargetPropertyConstructionTypeEnum.NEW_BUILDING,
    label: "target-property:construction-type.newBuild",
  },
  {
    value: TargetPropertyConstructionTypeEnum.EXISTING_BUILDING,
    label: "target-property:construction-type.existingBuild",
  },
];

export const PROPERTY_TENURE = [
  {
    value: TargetPropertyTenureEnum.FREE_HOLD,
    label: "target-property:tenure.freeHold",
  },
  {
    value: TargetPropertyTenureEnum.CONDO,
    label: "target-property:tenure.condo",
  },
  {
    value: TargetPropertyTenureEnum.LEASE_HOLD,
    label: "target-property:tenure.leaseHold",
  },
];

export const SPACE_UNITS = [
  {
    value: SpaceUnitsEnum.SQ_FEET,
    label: "target-property:space-units.squarefeet",
  },
  {
    value: SpaceUnitsEnum.SQ_METERS,
    label: "target-property:space-units.squaremeters",
  },
];

export const HEATING_TYPE = [
  {
    value: HeatingTypeEnum.ELECTRIC,
    label: "target-property:heating.electric",
  },
  {
    value: HeatingTypeEnum.FORCED_AIR_OIL_GAS_ELECTRIC,
    label: "target-property:heating.forcedAirOilGasElectric",
  },
  {
    value: HeatingTypeEnum.HOT_WATER,
    label: "target-property:heating.hotWater",
  },
  {
    value: HeatingTypeEnum.OTHER,
    label: "target-property:heating.other",
  },
];

export const SEWAGE_TYPE = [
  {
    value: SewageTypeEnum.MUNICIPAL,
    label: "target-property:sewage.municipal",
  },
  { value: SewageTypeEnum.SEPTIC, label: "target-property:sewage.septic" },
  {
    value: SewageTypeEnum.HOLDING_TANK,
    label: "target-property:sewage.holdingTank",
  },
];

export const WATER_TYPE = [
  { value: WaterTypeEnum.MUNICIPAL, label: "target-property:water.municipal" },
  { value: WaterTypeEnum.WELL, label: "target-property:water.well" },
];

export const GARAGE_SIZETYPE = [
  {
    value: GarageSizeEnum.SINGLE,
    label: "target-property:garage.space.single",
  },
  {
    value: GarageSizeEnum.DOUBLE,
    label: "target-property:garage.space.double",
  },
  {
    value: GarageSizeEnum.TRIPLE,
    label: "target-property:garage.space.triple",
  },
];

export const GARAGE_TYPE = [
  {
    value: GarageTypeEnum.DETACHED,
    label: "target-property:garage.type.detached",
  },
  {
    value: GarageTypeEnum.ATTACHED,
    label: "target-property:garage.type.attached",
  },
];

export const CO_OWNERSHIP_FEES_FREQENCY = [
  {
    value: CoOwnershipFeesFrequency.MONTHLY,
    label: "form:frequency.monthly",
  },
  {
    value: CoOwnershipFeesFrequency.ANNUALLY,
    label: "form:frequency.annually",
  },
];

export const UNIT_TYPE = [
  { value: UnitType.APARTMENT, label: "target-property:unitType.apartment" },
  { value: UnitType.PENTHOUSE, label: "target-property:unitType.penthouse" },
  { value: UnitType.UNIT, label: "target-property:unitType.unit" },
];

export const FUNDS_PURPOSE = [
  {
    value: FundPurposeEnum.HOME_IMPROVEMENT,
    label: "refinance-property:fundPurpose.homeImprovement",
  },
  {
    value: FundPurposeEnum.DEBT_CONSOLIDATION,
    label: "refinance-property:fundPurpose.debtConsolidation",
  },
  {
    value: FundPurposeEnum.INVESTMENT,
    label: "refinance-property:fundPurpose.investment",
  },
  {
    value: FundPurposeEnum.GIFT_TO_RELATIVE,
    label: "refinance-property:fundPurpose.giftToRelative",
  },
];
